// Field types supported by the form builder
export type FieldType =
  | 'text'
  | 'select'
  | 'boolean'
  | 'email'
  | 'phone_number'
  | 'number'
  | 'textarea'
  | 'checkbox'
  | 'ai'
  | 'date'
  | 'time'
  | 'http_request'
  | 'customer_verify'
  | 'file'
  | 'url';

// HTTP methods for HTTP Request fields
export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';

// Configuration for HTTP Request fields
export interface HttpRequestConfig {
  url: string;
  method: HttpMethod;
  headers?: { key: string; value: string }[];
  body?: string; // JSON string, supports {fieldId} placeholders
}

// Configuration for File Upload fields
export interface FileFieldConfig {
  acceptedTypes?: string[]; // extensions, e.g. ['pdf', 'png']
  maxFiles?: number;
  maxSizeMb?: number;
}

// Backend-enforced hard caps for file upload fields — admin config is
// clamped to these regardless of what is saved
export const FILE_UPLOAD_WHITELIST = ['pdf', 'doc', 'docx', 'txt', 'png', 'jpg', 'jpeg'];
export const FILE_UPLOAD_MAX_FILES = 5;
export const FILE_UPLOAD_MAX_SIZE_MB = 10;

// Validation types for form fields
export type ValidationType =
  | 'MIN_LENGTH'
  | 'MAX_LENGTH'
  | 'EMAIL'
  | 'PHONE'
  | 'URL'
  | 'REGEX'
  | 'NUMBER_RANGE'
  | 'REQUIRED';

// URL pattern shared by the preview; the agent backend enforces its own copy
export const URL_VALIDATION_REGEX =
  /^(https?:\/\/)?([a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}(:\d{1,5})?(\/\S*)?$/;

// Validation rule for a field
export interface FieldValidation {
  type: ValidationType;
  value?: string | number;
  min?: number;
  max?: number;
  errorMessage: string;
}

// Range constraint for number fields
export interface RangeType {
  min: number;
  max: number;
  step?: number;
}

// Option for select fields
export interface FieldOption {
  label: string;
  value: string;
}

// Conditional visibility operators — MUST match the agent backend's
// ConditionalRule union (cac-phase3-agent-booking/src/models/form.model.ts);
// the form JSON reaches the agent verbatim
export type ConditionalOperator =
  | 'equals'
  | 'notEquals'
  | 'contains'
  | 'in'
  | 'greaterThan'
  | 'lessThan';

// Conditional display logic ("show this field only when X <op> Y")
export interface FieldConditional {
  dependsOn: string;
  showWhen: {
    operator: ConditionalOperator;
    value: string | string[] | number;
  };
}

// Field types that can be used as conditional sources in the builder UI
export const CONDITIONAL_SOURCE_TYPES: FieldType[] = ['select', 'boolean', 'checkbox', 'number'];

// Form field definition
export interface FormField {
  fieldId: string;
  label: string;
  fieldType: FieldType;
  required: boolean;
  placeholder?: string;
  options?: FieldOption[];
  validation?: FieldValidation;
  rangeType?: RangeType;
  conditional?: FieldConditional;
  aiPrompt?: string;
  showResultToUser?: boolean; // For AI fields: when false, result is computed silently
  httpRequestConfig?: HttpRequestConfig; // For http_request fields
  verificationMethod?: 'email' | 'phone' | 'both'; // For customer_verify fields
  fileConfig?: FileFieldConfig; // For file upload fields
  order: number;
}

// Main Form interface
export interface Form {
  id?: number;
  uuid?: string; // API uses uuid for GET/PUT/DELETE operations
  formId: string;
  clientId: string;
  formName: string;
  triggerPrompt: string;
  description: string;
  requiresVerification: boolean;
  fields: FormField[];
  saveLocal: boolean;
  successMessage: string;
  errorMessage: string;
  sendEmailCopy?: boolean; // Send a copy of each form submission to email(s)
  emailIds?: string[]; // Email addresses that receive submission copies
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

// DTO for creating a new form
export interface CreateFormDTO {
  formName: string;
  triggerPrompt: string;
  description: string;
  requiresVerification?: boolean;
  fields: FormField[];
  successMessage: string;
  errorMessage: string;
  sendEmailCopy?: boolean;
  emailIds?: string[];
}

// DTO for updating an existing form
export interface UpdateFormDTO extends Partial<CreateFormDTO> {
  isActive?: boolean;
}

// Response wrapper for API calls
export interface FormResponse {
  data: Form[];
}

export interface SingleFormResponse {
  data: Form;
}

// Redux state for forms
export interface FormState {
  items: Form[];
  selectedForm: Form | null;
  isLoading: boolean;
  error: string | null;
}

// Form builder node data for ReactFlow
export interface FormFieldNodeData {
  field: FormField;
  onEdit: (fieldId: string) => void;
  onDelete: (fieldId: string) => void;
}

// AI field node data extends FormFieldNodeData with all non-AI fields for reference
export interface AIFieldNodeData extends FormFieldNodeData {
  allFields: FormField[];
}

// Customer variables available after verification
export const CUSTOMER_VARIABLES = [
  { id: 'customer_name', label: 'Customer Name', placeholder: '{customer_name}' },
  { id: 'customer_email', label: 'Customer Email', placeholder: '{customer_email}' },
  { id: 'customer_phone', label: 'Customer Phone', placeholder: '{customer_phone}' },
];

// UI-only type that includes 'range' as a separate toolbox item
export type ToolboxFieldType = FieldType | 'range';

// Draggable field type for the toolbox
export interface DraggableFieldType {
  type: ToolboxFieldType;
  label: string;
  icon: string;
  defaultField: Partial<FormField>;
}

// Form submission value from API
export interface FormSubmission {
  uuid: string;
  formId: string;
  customerName: string;
  contact: string;
  formValues: Record<string, string>;
  serviceType: string;
  createdAt: string;
  updatedAt: string;
}

// Form values list response
export interface FormValuesListResponse {
  data: FormSubmission[];
  count?: number;
}
